import { useEffect, useState, useRef } from 'react'
import { withRouter } from 'react-router'
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { useQuery } from 'Hooks'
import { connect } from 'react-redux'
import Feather from '@sysunite/react-feather'
import { QuestionCircleOutlined, SaveOutlined } from '@ant-design/icons'
import AceEditor from 'react-ace'
import { ResizableBox } from 'react-resizable'
import { Button, Tooltip, message, Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { WeaverQueryHelperModal } from 'Components'
import { setCurrentItem, setTab, setTabDisabled } from 'Actions/explore'
import { useHotKeys } from 'Hooks'
import {
  QUERY
  VIEWTYPE
} from 'Types'
import "ace-builds/src-noconflict/mode-perl"
import "ace-builds/src-noconflict/theme-solarized_light"
import langTools from "ace-builds/src-noconflict/ext-language_tools"
import weaverQueryCompleter from './weaverQueryCompleter'
import checkForEasterEggs from './easterEggs'
import './GraphConsole.scss'

defaultQuery = """
  # Gets all elements with an outgoing 'type' relation, fully loads all adjacent nodes.
  # (both incoming, and outgoing relations are loaded).

  hasRelationOut 'type'
  selectOut '*'
  selectIn '*'
  limit 50
"""

defaultSparql = """
  # Fully loads all nodes.

  SELECT DISTINCT ?node
  WHERE {
    ?node ?key ?to
  }
"""

GraphConsole = ({ currentBranch, openInConsole, isOpen, left, fullWidth, setCurrentItem, setTab, setTabDisabled, tabIsLocked, darkModeEnabled }) ->

  editorRef = useRef()
  tabIsLockedRef = useRef(tabIsLocked)
  params = useQuery()
  history = useHistory()
  { url } = useRouteMatch()
  { hash }  = useLocation()

  useEffect( ->
    tabIsLockedRef.current = tabIsLocked
    undefined
  , [tabIsLocked])

  [isOpen, setIsOpen] = useState(false)
  [height, setHeight] = useState(200)
  [consoleQuery, setConsoleQuery] = useState({ type: 'QueryBuilder', query: defaultQuery })
  [attrKeys, setAttrKeys] = useState([])
  [relKeys, setRelKeys] = useState([])
  [isLoading, setIsLoading] = useState(true)
  [helperIsVisible, setHelperIsVisible] = useState(false)
  iconType = if isOpen then 'x' else 'terminal'

  width = fullWidth - left

  useEffect( ->
    if openInConsole?
      setIsOpen(true)
      setConsoleQuery(openInConsole)
      fireQuery(openInConsole.query)
    undefined
  , [openInConsole])

  useEffect( ->
    ( ->
      relKeyBindings = await Weaver.MetaQueries.relationsCount({ limit: 99999, branch: currentBranch })
      setRelKeys(relKeyBindings.map(({ key }) -> key ))
      attrKeyBindings = await Weaver.MetaQueries.attributesCount({ limit: 99999, branch: currentBranch })
      setAttrKeys(attrKeyBindings.map(({ key }) -> key ))
      setIsLoading(false)
    )()
    undefined
  , [])

  useEffect( ->
    if isOpen
      editorRef.current?.editor.focus()
    else
      editorRef.current?.editor.blur()
    undefined
  , [isOpen])

  useHotKeys([{
    sequence: 'esc'
    fn: -> setIsOpen(false)
    expr: isOpen
    temporary: true
    watchers: [isOpen]
  }, {
    sequence: 'mod+d'
    fn: -> setIsOpen(!isOpen)
    temporary: true
    watchers: [isOpen]
  }])

  switchQueryType = ->
    if consoleQuery.type is 'QueryBuilder'
      setConsoleQuery({ type: 'Sparql', query: defaultSparql })
    else
      setConsoleQuery({ type: 'QueryBuilder', query: defaultQuery })

  getCurrentTimeInMillis = ->
    new Date().getMilliseconds()

  getValidQuery = (q) ->
    { query, type } = q
    if type is 'Sparql'
      await new Weaver.Sparql(query).limit(0).findNodes()
      new Weaver.Sparql(query)
    else
      new Weaver.QueryBuilder(query).build()

  getEditorValues = (editor) ->
    mode = editor.getOption('mode').split('/')[2]
    type = if editor.getOption('mode').split('/')[2] is 'sparql' then _.startCase(mode) else 'QueryBuilder'
    { type, query: editor.getValue()}

  fireQuery = (q) ->
    if eEggUrl = checkForEasterEggs(q)
      return history.push({ pathname: eEggUrl, search: params.toString() })
    try
      validatedQ = await getValidQuery(q)
      setCurrentItem({
        type: QUERY
        ranAt: getCurrentTimeInMillis()
        data: {
          query: validatedQ
        }
      })
      setTabDisabled({ key: 'elements', disabled: false })
      setTabDisabled({ key: 'attributes', disabled: true })
      params.set('tab', 'elements')
      history.replace({ pathname: url, search: params.toString(), hash })
      setTab('elements') unless tabIsLockedRef.current
    catch e
      console.error(e)
      message.error('Your query is badly formed. Click the help icon to see the query reference.')

  loadingIcon = <LoadingOutlined style={{ fontSize: 44 }} spin />

  <div style={{ left, width }} className="graph-console-container">
    <div style={{ height: if isOpen then height else null }} className="console-button-container">
      <Tooltip title={if isOpen then 'Close (Esc)' else 'Open graph console (Cmd/Ctrl + D)'} placement="left">
        <Button
          data-testid="open-close-graph-console"
          icon={<Feather size="1em" type={iconType} />}
          style={{ pointerEvents: 'auto', zIndex: 9999 }}
          onClick={ -> setIsOpen(!isOpen)}
        />
      </Tooltip>

      {
        if isOpen
          <>
            <Tooltip title={consoleQuery.type} placement="left">
              <Button
                data-testid="query-language-switcher-button"
                icon={if consoleQuery.type is 'Sparql' then 'S' else 'Q'}
                style={{ pointerEvents: 'auto', zIndex: 9999 }}
                onClick={ -> switchQueryType()}
              />
            </Tooltip>
            <Tooltip title="Help with queries" placement="left">
              <Button
                data-testid="query-helper-button"
                icon={<QuestionCircleOutlined />}
                style={{ pointerEvents: 'auto', zIndex: 9999 }}
                onClick={ -> setHelperIsVisible(true)}
              />
            </Tooltip>
            <Tooltip title="Save query as view" placement="left">
              <Button
                data-testid="save-query-button"
                icon={<SaveOutlined />}
                style={{ pointerEvents: 'auto', zIndex: 9999 }}
                onClick={ ->
                  { current: { editor } } = editorRef
                  { query, type } = getEditorValues(editor)
                  history.push("#{url}/settings/views/new?viewType=graphView&queryType=#{type}&query=#{encodeURIComponent(query)}")
                }
              />
            </Tooltip>
            <div style={{ flex: '1 1 auto' }} />
            <Tooltip title="Run query (Cmd/Ctrl + Enter)" placement="left">
              <Button
                data-testid="fire-console-query-button"
                icon={<Feather type='corner-down-left' />}
                style={{ pointerEvents: 'auto', zIndex: 9999 }}
                onClick={->
                  {current: { editor }} = editorRef
                  fireQuery(getEditorValues(editor))
                }
              />
            </Tooltip>
          </>
      }
    </div>
    <ResizableBox
      height={height}
      width={width}
      minConstraints={[width, 50]}
      maxConstraints={[width, 500]}
      onResize={(event, { size }) -> setHeight(size.height) }
      style={{
        display: isOpen or 'none'
        position: 'absolute'
        bottom: 0
        pointerEvents: 'auto'
      }}
      handle={
        <div
          style={{
            width: '100%',
            height: 3
            cursor: 'ns-resize'
            position: 'absolute'
            top: -3
          }}
        />
      }
      handleSize={[width, 15]}
      resizeHandles={['n']}
    >
      <div style={{ top: (if isOpen then -53 else 7), height, width }}>
        { if isLoading
            <div
              style={{
                backgroundColor: darkModeEnabled && '#002B36' || '#FDF6E3'
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
              <Spin indicator={loadingIcon} style={{ display: 'flex', alignItems: 'center' }} />
            </div>
          else
            <AceEditor
              placeholder="Enter your query here"
              mode={if consoleQuery.type is 'QueryBuilder' then 'perl' else 'sparql'}
              theme={darkModeEnabled && "solarized_dark" || "solarized_light"}
              name="Graph view devtools"
              fontSize={16}
              showGutter={true}
              highlightActiveLine={true}
              value={consoleQuery.query}
              onChange={(e) -> setConsoleQuery({ consoleQuery..., query: e })}
              height={(height) + 'px'}
              width={width + 'px'}
              ref={editorRef}
              setOptions={{
                showPrintMargin: false,
                enableBasicAutocompletion: [weaverQueryCompleter(attrKeys, relKeys)]
                enableLiveAutocompletion: [weaverQueryCompleter(attrKeys, relKeys)],
                showLineNumbers: true,
                tabSize: 2,
              }}
              commands={[{
                name: 'runQuery',
                bindKey: { win: 'Ctrl-enter', mac: 'Command-enter' },
                exec: (editor) ->
                  current = await Weaver.getCoreManager().currentBranch
                  params.set('branch', current.id())
                  fireQuery(getEditorValues(editor))
              }, {
                name: 'toggleDevtools',
                bindKey: { win: 'Ctrl-d', mac: 'Command-d' },
                exec: -> setIsOpen(false)
              }, {
                name: 'hideDevtools',
                bindKey: { win: 'esc', mac: 'esc' },
                exec: -> setIsOpen(false)
              }]}
              style={{ borderTop: '1px solid var(--borderColor)'}}
            />
        }

      </div>
    </ResizableBox>
    <WeaverQueryHelperModal
      queryType={consoleQuery.type}
      visible={helperIsVisible}
      setVisible={setHelperIsVisible}
    />
  </div>

mapStateToProps = ({ branches, explore }) ->
  {
    currentBranch: branches.currentBranch,
    fullWidth: explore.width,
    tabIsLocked: explore.tabIsLocked
    openInConsole: explore.openInConsole
    darkModeEnabled: explore.darkModeEnabled
  }

export default connect(
  mapStateToProps,
  { setCurrentItem, setTab, setTabDisabled }
)(withRouter(GraphConsole))
